import { useQuery, type QueryKey } from '@tanstack/react-query';
import service from '@Service';
import type {
  ShiftReportSummary,
  ShiftReportDetail,
} from '@Types/shiftReport';

export const shiftReportKeys = {
  forSoul: (soulId: string) => ['shift-reports', 'soul', soulId] as const,
  detail: (id: string) => ['shift-report', id] as const,
};

// Reports land once a day by cron; manual runs invalidate the list themselves and
// poll through refetchInterval, which ignores staleTime. Refocusing the window or
// remounting the desk inside this window reuses the cached page.
export const REPORT_LIST_STALE_MS = 30 * 1000;

export function useShiftReports({
  soulId,
  enabled = true,
  refetchInterval = false,
}: {
  soulId?: string;
  enabled?: boolean;
  refetchInterval?: number | false;
}) {
  return useQuery<ShiftReportSummary[]>({
    queryKey: shiftReportKeys.forSoul(soulId!) as unknown as QueryKey,
    queryFn: () => service.getShiftReports({ soulId: soulId! }),
    enabled: enabled && !!soulId,
    refetchInterval,
    staleTime: REPORT_LIST_STALE_MS,
  });
}

export function useShiftReport({
  id,
  enabled = true,
}: {
  id?: string;
  enabled?: boolean;
}) {
  return useQuery<ShiftReportDetail>({
    queryKey: shiftReportKeys.detail(id!) as unknown as QueryKey,
    queryFn: () => service.getShiftReport(id!),
    enabled: enabled && !!id,
    // A persisted report never changes; reopening the sheet or refocusing the
    // window must not re-download it.
    staleTime: Infinity,
  });
}
